/**
 * AuthEventService.ts — OPERATOR IDENTITY™ Phase 1 (Build 2.06.0).
 *
 * Publishes authentication lifecycle events onto the Core Event Bus.
 * This service is the ONLY publisher of OperatorSignedIn, OperatorSignedOut,
 * SessionRestored, and SessionExpired. It never performs auth itself —
 * it is called by the UI (Login page, Shell) after auth mutations succeed.
 *
 * Architecture rule: modules may subscribe to these events later; do NOT
 * add direct calls to modules from here.
 */

import { roamerCore } from './core/RoamerCore';
import type { User } from '@workspace/api-client-react';

export interface AuthSessionMeta {
  operatorId: number;
  email: string;
  displayName?: string | null;
  rememberMe?: boolean;
  createdAt: string; // ISO string from /api/auth/me
}

/** Track session restore time for Dev Center diagnostics. */
let sessionRestoreTime: string | null = null;

export function getSessionRestoreTime(): string | null {
  return sessionRestoreTime;
}

/**
 * Call after a successful login or registration.
 */
export function publishSignedIn(user: User & { rememberMe?: boolean }): void {
  const now = Date.now();
  try {
    roamerCore.publish('OperatorSignedIn', 'auth-event-service', {
      operatorId: user.id,
      email: user.email,
      displayName: user.displayName ?? null,
      rememberMe: user.rememberMe ?? true,
    }, now);
  } catch {
    // Best-effort — never block the UI
  }
}

/**
 * Call after logout completes (before redirect).
 */
export function publishSignedOut(operatorId: number): void {
  const now = Date.now();
  try {
    roamerCore.publish('OperatorSignedOut', 'auth-event-service', {
      operatorId,
    }, now);
  } catch {
    // Best-effort
  }
}

/**
 * Call when /api/auth/me succeeds on mount (session restore from cookie).
 */
export function publishSessionRestored(user: User): void {
  const now = Date.now();
  sessionRestoreTime = new Date(now).toISOString();
  const createdMs = user.createdAt ? new Date(user.createdAt).getTime() : 0;
  const sessionAge = createdMs > 0 ? now - createdMs : 0;
  try {
    roamerCore.publish('SessionRestored', 'auth-event-service', {
      operatorId: user.id,
      email: user.email,
      sessionAge,
    }, now);
  } catch {
    // Best-effort
  }
}

/**
 * Call when /api/auth/me returns 401 (session expired or cookie gone).
 */
export function publishSessionExpired(): void {
  const now = Date.now();
  try {
    roamerCore.publish('SessionExpired', 'auth-event-service', {}, now);
  } catch {
    // Best-effort
  }
}
